import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { formatLiveMatchDate, liveMatchHeadline } from '@/utils/liveSessionDisplay';
import type { LiveSession } from '@/types';

interface Props {
  session: LiveSession;
  compact?: boolean;
  inverted?: boolean;
  eventCount?: number;
}

export default function LiveMatchHeader({ session, compact = false, inverted = false, eventCount }: Props) {
  const headline = liveMatchHeadline(session);
  const dateLabel = formatLiveMatchDate(session.match_date);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  return (
    <Stack spacing={compact ? 0.35 : 0.5} alignItems="center" sx={{ width: '100%', px: 0.5 }}>
      {session.competition_name && (
        <Chip
          size="small"
          label={session.competition_name}
          color={inverted ? 'default' : 'secondary'}
          variant="outlined"
          sx={{
            height: compact ? 22 : 24,
            fontWeight: 700,
            ...(inverted
              ? { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.35)' }
              : {}),
          }}
        />
      )}
      <Typography
        variant={compact ? 'subtitle1' : 'h6'}
        fontWeight={800}
        textAlign="center"
        sx={{ lineHeight: 1.15, color: inverted ? '#fff' : 'inherit' }}
      >
        {headline}
      </Typography>
      {(dateLabel || session.match_duration_minutes || eventCount != null) && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent="center">
          {dateLabel && mounted && (
            <Typography
              variant="caption"
              color={inverted ? 'rgba(255,255,255,0.65)' : 'text.secondary'}
              fontWeight={600}
            >
              {dateLabel}
            </Typography>
          )}
          {session.match_duration_minutes && (
            <Typography
              variant="caption"
              color={inverted ? 'rgba(255,255,255,0.65)' : 'text.secondary'}
              fontWeight={600}
            >
              {dateLabel ? '· ' : ''}
              {session.match_duration_minutes} 分鐘
            </Typography>
          )}
          {eventCount != null && (
            <Typography
              variant="caption"
              color={inverted ? 'rgba(255,255,255,0.65)' : 'text.secondary'}
              fontWeight={600}
            >
              {(dateLabel || session.match_duration_minutes) ? '· ' : ''}
              {eventCount} 個標記
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/** Inline team scoreboard strip for fullscreen mode */
export function LiveMatchScoreboard({ session }: { session: LiveSession }) {
  const home = session.home_team?.trim() || '主隊';
  const away = session.away_team?.trim() || '客隊';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        textAlign: 'center',
      }}
    >
      <Typography fontWeight={800} noWrap sx={{ color: '#fff', fontSize: '0.95rem' }}>
        {home}
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 700, px: 0.5 }}>vs</Typography>
      <Typography fontWeight={800} noWrap sx={{ color: '#fff', fontSize: '0.95rem' }}>
        {away}
      </Typography>
    </Box>
  );
}
